import { notifyError } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import { practiceService } from '@/core/services/practice';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import type {
  AtbDrilldown,
  AtbDrilldownJournalLine,
  StatementArea,
  TrialBalanceVarianceQuery,
  TrialBalanceVarianceResponse,
  VarianceLine,
} from '@/core/models/phase1.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { Select } from '@/kit/Select';
import { formatBracketed, formatNatural, formatNaturalPercent, toNatural } from '@/core/format/naturalBalance';
import { ExportMenu, SearchField, fmt2, periodDash, useAlive, useClientInit, useLive } from './common';
import { reversalLinkParts } from './reversalLinks';

const ALL = -1;

const YEAR_OPTIONS = [
  { id: 0, name: 'Current year only' },
  { id: 1, name: 'Plus 1 prior year' },
  { id: 2, name: 'Plus 2 prior years' },
  { id: 3, name: 'Plus 3 prior years' },
  { id: 4, name: 'Plus 4 prior years' },
  { id: 5, name: 'Plus 5 prior years' },
];

const AREA_OPTIONS = [
  { id: ALL, name: 'All statements' },
  { id: 0, name: 'Profit and loss' },
  { id: 1, name: 'Balance sheet' },
  { id: 2, name: 'Capital and reserves' },
  { id: 4, name: 'Notes' },
];

const journalStatusName = (status: number | null | undefined) =>
  status === 0 ? 'Draft' : status === 1 ? 'Posted' : status === 2 ? 'Reversed' : status === 3 ? 'Reversal' : '';

/** 'reversal of #n' / 'reversed by #m' when the API sends the linked journal numbers (with the other period's dates when it is another period). */
const reversalLinkText = (line: AtbDrilldownJournalLine) => reversalLinkParts(line, false).join(', ');

interface Model {
  dataSource: VarianceLine[];
  data: TrialBalanceVarianceResponse | null;
  periods: PeriodRow[];
  selectedPeriodId: string | null;
  years: number;
  areaFilter: number;
  searchText: string;
  includeZeroBalances: boolean;
  drilldown: AtbDrilldown | null;
  drilldownTitle: string;
  /** APT349: the natural side of the code being drilled into: its nominals' amounts are shown with it. */
  drilldownSign: number;
}

/**
 * AP-005 adjusted trial balance: source, journals, adjusted, prior and variance columns with multi-year alignment,
 * drill-down to the nominals and journals behind a line, and exports that come from the same dataset the screen
 * renders. (Angular TaxreturnAdjustedtrialbalanceComponent.)
 */
export function TaxreturnAdjustedtrialbalance() {
  const alive = useAlive();
  const [m, set, ref] = useLive<Model>({
    dataSource: [],
    data: null,
    periods: [],
    selectedPeriodId: null,
    years: 1,
    areaFilter: ALL,
    searchText: '',
    includeZeroBalances: false,
    drilldown: null,
    drilldownTitle: '',
    drilldownSign: 1,
  });

  const query = (): TrialBalanceVarianceQuery => {
    const s = ref.current;
    return {
      years: s.years,
      statementArea: s.areaFilter === ALL ? null : (s.areaFilter as StatementArea),
      search: s.searchText?.trim() || null,
      includeZeroBalances: s.includeZeroBalances,
    };
  };

  const load = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    set({ drilldown: null });
    try {
      const response = await phase1Service.getVariance(periodId, query());
      if (!alive.current) return;
      set({ data: response, dataSource: response.lines ?? [] });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  useClientInit(async (client) => {
    try {
      const response = await practiceService.getClientPeriods(client.id);
      if (!alive.current) return;
      const periods = response.periods ?? [];
      // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
      const selectedPeriodId = await resolveDefaultPeriodId(client.id, periods);
      set({ periods, selectedPeriodId });
      if (selectedPeriodId) {
        await load();
      } else {
        notifyError('Add an accounting period before viewing the trial balance.');
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  });

  /** Drill from a reporting-code line to the nominals and posted journals behind it. */
  const onDrilldown = async (line: VarianceLine) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      const detail = await phase1Service.getDrilldown(periodId, line.reportingCodeId);
      if (!alive.current) return;
      set({ drilldown: detail, drilldownTitle: `${line.reportingCode} - ${line.description}`, drilldownSign: line.normalSign });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /** format: 1 CSV, 2 Excel, 3 PDF. */
  const onExport = async (format: number) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    try {
      await phase1Service.exportVariance(periodId, query(), { format, isPrint: false });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const yearLabel = (index: number) => (m.data && m.data.periods.length > index ? m.data.periods[index].label : '');

  const yearAmount = (line: VarianceLine, index: number) =>
    line.yearAmounts && line.yearAmounts.length > index ? line.yearAmounts[index] : 0;

  const columns: DataTableColumn<VarianceLine>[] = [
    {
      id: 'code',
      header: 'Code',
      cell: (element) => (
        <a className="link" onClick={() => void onDrilldown(element)}>
          {element.reportingCode}
        </a>
      ),
    },
    { id: 'description', header: 'Description', cell: (element) => element.description },
    // APT349: every amount is shown on its reporting code's natural side - positive without a sign when the balance is where the code normally lies,
    // in brackets when it is on the other side. The stored amounts (debits positive) are unchanged; only what is shown follows the code.
    { id: 'source', header: 'Source', cell: (element) => formatNatural(element.sourceAmount, element.normalSign) },
    { id: 'journals', header: 'Journals', cell: (element) => formatNatural(element.journalAmount, element.normalSign) },
    { id: 'adjusted', header: 'Adjusted', cell: (element) => formatNatural(element.adjustedAmount, element.normalSign) },
    { id: 'prior', header: 'Prior', cell: (element) => formatNatural(element.priorAmount, element.normalSign) },
    {
      id: 'variance',
      header: 'Variance',
      cell: (element) => (
        <span className={toNatural(element.varianceAmount, element.normalSign) < 0 ? 'text-red' : ''}>
          {formatNatural(element.varianceAmount, element.normalSign)}
        </span>
      ),
    },
    {
      id: 'variancepercent',
      header: 'Variance %',
      cell: (element) => formatNaturalPercent(element.variancePercent, element.normalSign),
    },
  ];
  // One column per comparative year actually returned (index 0 is the current period).
  const comparatives = m.data ? m.data.periods.length - 1 : 0;
  for (let index = 1; index <= Math.min(comparatives, 5); index++) {
    columns.push({
      id: `year${index}`,
      header: yearLabel(index),
      cell: (element) => formatNatural(yearAmount(element, index), element.normalSign),
    });
  }

  return (
    <SideMenuPage menu="task">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Accounting period"
              items={m.periods}
              bindLabel="fromDate"
              bindValue="id"
              clearable={false}
              value={m.selectedPeriodId}
              onChange={(value) => {
                set({ selectedPeriodId: (value as string | null) ?? null });
                rememberChosenPeriod(value as string | null);
                void load();
              }}
              renderLabel={periodDash}
              renderOption={periodDash}
            />

            <SearchField
              value={m.searchText}
              onValueChange={(searchText) => set({ searchText })}
              onCommit={() => void load()}
            />

            <Checkbox
              checked={m.includeZeroBalances}
              onChange={(event) => {
                set({ includeZeroBalances: event.target.checked });
                void load();
              }}
            >
              Include nil balances
            </Checkbox>
          </div>

          <div className="content-header-right mb-1">
            <Select
              className="w-200"
              openOnFocus
              panelClass="myPanelClass"
              items={AREA_OPTIONS}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.areaFilter}
              onChange={(value) => {
                set({ areaFilter: value as number });
                void load();
              }}
            />
            <Select
              className="w-200"
              openOnFocus
              panelClass="myPanelClass"
              items={YEAR_OPTIONS}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.years}
              onChange={(value) => {
                set({ years: value as number });
                void load();
              }}
            />
            <ExportMenu onExport={(format) => void onExport(format)} />
          </div>
        </div>

        {m.data && (
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              {/* APT349: a total spans codes of both sides, so it has no natural side: the stored sign, a negative in brackets. */}
              <span>Current {formatBracketed(m.data.totals.totalCurrent)}</span>
              <span>&nbsp;&middot;&nbsp;Prior {formatBracketed(m.data.totals.totalPrior)}</span>
              <span>&nbsp;&middot;&nbsp;Variance {formatBracketed(m.data.totals.totalVariance)}</span>
            </div>
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable columns={columns} rows={m.dataSource} rowKey={(row) => row.reportingCodeId} stickyHeader />
        </div>

        {m.drilldown && (
          <div className="content-body">
            <div className="content-header mb-0">
              <div className="content-header-left mb-1">
                <h3 className="m-0">{m.drilldownTitle}</h3>
              </div>
              <div className="content-header-right mb-1">
                <Button title="Close" aria-label="Close" onClick={() => set({ drilldown: null })}>
                  <Icon name="close" />
                </Button>
              </div>
            </div>

            <p className="fw-bold">Nominals</p>
            <table className="wp-100">
              <tbody>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Source</th>
                  <th>Journals</th>
                  <th>Adjusted</th>
                  <th>Prior</th>
                </tr>
                {(m.drilldown.nominals ?? []).map((nominal) => (
                  <tr key={nominal.nominalAccountId}>
                    <td>{nominal.code}</td>
                    <td>{nominal.displayDescription || nominal.description}</td>
                    {/* APT349 follow-up: a nominal's side is its own category's (accumulated depreciation is credit-natured inside an asset code and shows
                        positive); the clicked code's side is only the fallback for a nominal whose chart account is unknown. */}
                    <td>{formatNatural(nominal.sourceAmount, nominal.normalSign ?? m.drilldownSign)}</td>
                    <td>{formatNatural(nominal.journalAmount, nominal.normalSign ?? m.drilldownSign)}</td>
                    <td>{formatNatural(nominal.adjustedAmount, nominal.normalSign ?? m.drilldownSign)}</td>
                    <td>{formatNatural(nominal.priorAmount, nominal.normalSign ?? m.drilldownSign)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="fw-bold">Journal lines (including reversals)</p>
            <div style={{ overflowX: 'auto' }}>
              <table className="wp-100">
                <tbody>
                  <tr>
                    <th>No.</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Nominal</th>
                    <th>Narrative</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                  {(m.drilldown.journalLines ?? []).map((line) => (
                    <tr key={line.journalLineId}>
                      <td>{line.journalNo}</td>
                      <td>{line.journalDate}</td>
                      <td>{line.typeCode}</td>
                      <td>
                        {journalStatusName(line.journalStatus ?? line.status)}
                        {reversalLinkText(line) && (
                          <span className="ml-1" data-testid="drilldown-reversal-link">
                            <InfoHint text={reversalLinkText(line)} placement="right" />
                          </span>
                        )}
                      </td>
                      <td>{line.nominalCode}</td>
                      <td>{line.lineNarrative || line.narrative}</td>
                      <td>{fmt2(line.debit)}</td>
                      <td>{fmt2(line.credit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
